import React, { useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import type { ReviewSuggestion } from '../seededOnboardingReviewState';
import { Dropdown } from '../ui/Dropdown';

interface ObjectionVariationMergeDialogProps {
  source: ReviewSuggestion;
  candidates: ReviewSuggestion[];
  initialTargetTempId?: string;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onMerge: (targetTempId: string) => void;
}

const ObjectionVariationMergeDialog: React.FC<ObjectionVariationMergeDialogProps> = ({
  source,
  candidates,
  initialTargetTempId,
  isSaving,
  error,
  onClose,
  onMerge,
}) => {
  const [targetTempId, setTargetTempId] = useState(initialTargetTempId || '');
  const target = useMemo(
    () => candidates.find((candidate) => candidate.tempId === targetTempId) || null,
    [candidates, targetTempId],
  );

  return (
    <Modal
      open
      hideBackdrop
      onClose={() => { if (!isSaving) onClose(); }}
      className="modal-backdrop kb-modal-overlay z-[1700]"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !isSaving) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-objection-title"
        className="modal-panel kb-dialog w-full max-w-lg"
      >
        <div className="border-b border-hairline p-4">
          <h2 id="merge-objection-title" className="heading-4 kb-dialog-title">Add as variation</h2>
          <p className="mt-1 text-sm text-ink-secondary">
            Attach this buyer wording to another objection recommendation.
          </p>
        </div>

        <div className="space-y-4 p-4">
          <div className="rounded-control border border-hairline bg-surface-subtle p-3">
            <p className="text-xs font-semibold text-ink-muted">Buyer wording to move</p>
            <p className="mt-1 text-sm font-medium text-ink">{source.question}</p>
          </div>

          <div>
            <label id="objection-variation-target-label" className="mb-1.5 block text-sm font-medium text-ink-secondary">
              Objection recommendation
            </label>
            <Dropdown
              id="objection-variation-target"
              labelledBy="objection-variation-target-label"
              width="field"
              autoFocus
              placeholder="Choose the response to keep"
              value={targetTempId}
              onChange={setTargetTempId}
              disabled={isSaving}
              options={candidates.map((candidate) => ({ value: candidate.tempId, label: candidate.question }))}
            />
          </div>

          {target && (
            <div className="rounded-control border border-success bg-success-tint p-3">
              <p className="text-xs font-semibold text-ink">Response that will be used</p>
              <p className="mt-1 text-sm text-ink-secondary">{target.answer}</p>
            </div>
          )}

          <p className="text-xs leading-caption text-ink-muted">
            This draft card will be removed. Its buyer wording and variations will use the selected recommendation&apos;s response.
          </p>
          {candidates.length === 0 && (
            <p role="alert" className="text-sm text-error">
              This merge is no longer available. Close this dialog and refresh the draft.
            </p>
          )}
          {error && <p role="alert" className="text-sm text-error">{error}</p>}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-hairline p-4 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={isSaving} className="kb-button kb-button-secondary px-4 py-2.5 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { if (target) onMerge(target.tempId); }}
            disabled={!target || isSaving}
            className="kb-button kb-button-primary px-4 py-2.5 text-sm"
          >
            {isSaving ? 'Adding variation…' : 'Add variation'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ObjectionVariationMergeDialog;
